import type {
  LoginInput,
  PasswordChangeInput,
  TokenPairResponse,
} from '@greenostrov/shared';

const BASE = import.meta.env.VITE_API_ORIGIN ?? '';

export interface AdminDto {
  id: string;
  username: string;
  email: string;
  name: string;
  mustChangePassword: boolean;
}

class TokenStore {
  private accessToken: string | null = null;
  private refreshPromise: Promise<string | null> | null = null;

  get access(): string | null {
    if (this.accessToken) return this.accessToken;
    try {
      return window.localStorage.getItem('ga_access');
    } catch {
      return null;
    }
  }

  get refresh(): string | null {
    try {
      return window.localStorage.getItem('ga_refresh');
    } catch {
      return null;
    }
  }

  set(pair: TokenPairResponse): void {
    this.accessToken = pair.accessToken;
    try {
      window.localStorage.setItem('ga_access', pair.accessToken);
      window.localStorage.setItem('ga_refresh', pair.refreshToken);
      window.localStorage.setItem('ga_access_exp', pair.accessExpiresAt);
    } catch {
      // ignore
    }
  }

  clear(): void {
    this.accessToken = null;
    try {
      window.localStorage.removeItem('ga_access');
      window.localStorage.removeItem('ga_refresh');
      window.localStorage.removeItem('ga_access_exp');
    } catch {
      // ignore
    }
  }

  async refreshAccess(): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise;
    const refresh = this.refresh;
    if (!refresh) return null;

    this.refreshPromise = (async () => {
      try {
        const res = await fetch(`${BASE}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: refresh }),
        });
        if (!res.ok) {
          this.clear();
          return null;
        }
        const data = (await res.json()) as TokenPairResponse;
        this.set(data);
        return data.accessToken;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }
}

export const tokens = new TokenStore();

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  const access = tokens.access;
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (res.status === 401 && retry) {
    const fresh = await tokens.refreshAccess();
    if (fresh) return request<T>(path, init, false);
    throw new ApiError('unauthorized', 401);
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(typeof body?.error === 'string' ? body.error : 'request_failed', res.status);
  }
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
  }
}

export const api = {
  login: (input: LoginInput) =>
    request<TokenPairResponse & { admin: AdminDto }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  logout: () => {
    const refresh = tokens.refresh;
    if (!refresh) return Promise.resolve();
    return request<{ ok: boolean }>('/api/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken: refresh }),
    });
  },
  me: () => request<{ admin: AdminDto }>('/api/auth/me'),
  changePassword: (input: PasswordChangeInput) =>
    request<{ ok: boolean }>('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  listBookings: (params: { status?: string; q?: string; from?: string; to?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '' && v !== null) qs.set(k, String(v));
    });
    return request<{ items: unknown[]; total: number; limit: number; offset: number }>(
      `/api/bookings${qs.toString() ? `?${qs.toString()}` : ''}`,
    );
  },
  getBooking: (id: string) => request<Record<string, unknown>>(`/api/bookings/${id}`),
  updateBooking: (id: string, update: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/api/bookings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(update),
    }),
  deleteBooking: (id: string) =>
    request<{ ok: boolean }>(`/api/bookings/${id}`, { method: 'DELETE' }),

  listPricesAdmin: () => request<{ items: unknown[] }>('/api/prices/admin'),
  createPrice: (input: Record<string, unknown>) =>
    request<Record<string, unknown>>('/api/prices', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updatePrice: (id: string, input: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/api/prices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deletePrice: (id: string) =>
    request<{ ok: boolean }>(`/api/prices/${id}`, { method: 'DELETE' }),
};
